import {
  Box,
  Typography,
  Avatar,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationBell from "Components/Notification/NotificationBell";
import ImgPath from "Constants/Imgs";
import ColorData from "Components/CstmTheme/ColorData";
import { useDispatch, useSelector } from "react-redux";
import { clearUser } from "../../Redux/User/Action";
import { UserInitialStateType } from "DataTypes/Redux";
import SubTopBar, { menuItems } from "./SubTopBar";
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from "react";
import MobileDrawer from "Components/commonComponents/HederDrawer";
import SidebarDrawer from "Components/commonComponents/sidebarDrawer";
import { handleNavigation } from "Components/Header";
import { navItems } from "./Sidebar";
import { useNavigate } from "react-router-dom";
import { disconnectChatHub } from "../../Service/signalRService";
import { toast } from "react-toastify";

const Topbar = ({ onMenuClick }: { onMenuClick?: () => void }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { myInfo }: UserInitialStateType = useSelector((state: any) => state?.user);

  const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarDrawerOpen, setSidebarDrawerOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleLogout = async () => {
    await disconnectChatHub();
    localStorage.clear();
    dispatch(clearUser() as any);
    window.location.href = "/home.html";
  };
  const currentDateTime = dayjs().format('D MMM, YYYY / h:mmA');
  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    // Reset expanded items when drawer closes for better UX
    setExpandedItems({});
  }, []);
  const handleExpandClick = (id: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };


  return (
    <Box
      sx={{
        bgcolor: "rgba(255, 255, 255, 0.8)",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
        position: "sticky",
        top: 0,
        zIndex: 1000,
        minHeight: myInfo?.userType === "USER" ? "140px" : "80px",
      }}
      className="topbar-box"
    >
      <Box
        sx={{
          px: { xs: 2.5, sm: 4 },
          py: { xs: 1.5, sm: 2 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          minHeight: "80px",
        }}
      >
        {/* Left Section - Logo + Welcome */}
        <Box display="flex" alignItems="center" gap={2.5}>
          {isMobile && (
            <IconButton 
              onClick={() => setSidebarDrawerOpen(true)} 
              sx={{ color: ColorData.text.primary, bgcolor: "rgba(0,0,0,0.03)" }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Avatar 
            src={myInfo?.profileImageUrl || "/assets/images/dashboard-icons/profile-img.png"} 
            sx={{ 
              width: 45, 
              height: 45, 
              border: "2px solid #28316520",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
            }}
          />
          <Box>
            <Typography sx={{ 
              fontFamily: '"Outfit", sans-serif', 
              fontSize: "0.9rem", 
              color: "#64748b",
              fontWeight: 400
            }}>
              Welcome back,
            </Typography>
            <Typography sx={{ 
              fontFamily: '"Outfit", sans-serif', 
              fontSize: "1.1rem", 
              fontWeight: 600,
              color: "#1e293b"
            }}>
              {myInfo?.firstname || "Admin"}
            </Typography>
          </Box>
        </Box>

        {/* Right Section */}
        <Box display="flex" alignItems="center" gap={2}>
          {/* NOTIFICATION BELL */}
          <NotificationBell />

          {!isMobile && (
            <Box display="flex" alignItems="center" gap={3}>
              <Typography sx={{ 
                fontFamily: '"Inter", sans-serif',
                fontWeight: 500, 
                fontSize: "0.85rem",
                color: "#64748b",
                bgcolor: "#f1f5f9",
                px: 2,
                py: 0.5,
                borderRadius: "20px"
              }}>
                {currentDateTime}
              </Typography>
              <IconButton
                sx={{
                  cursor: "pointer",
                  color: "#e11d48",
                  fontSize: "0.9rem",
                  gap: 1,
                  borderRadius: "10px",
                  px: 2,
                  "&:hover": { backgroundColor: "rgba(225, 29, 72, 0.05)" },
                }}
                onClick={handleLogout}
              >
                <ImgPath.LogoutIcon sx={{ fontSize: 20 }} />
                <Typography sx={{ 
                  fontFamily: '"Outfit", sans-serif', 
                  fontWeight: 600,
                  fontSize: "0.95rem"
                }}>Logout</Typography>
              </IconButton>
            </Box>
          )}

          {isMobile && (
            <IconButton 
              onClick={() => setDrawerOpen(true)} 
              sx={{ color: ColorData.text.primary, bgcolor: "rgba(0,0,0,0.03)" }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {myInfo?.userType === "USER" && (
        <Box sx={{ borderTop: "1px solid #e0e0e0", bgcolor: "white" }}>
          <SubTopBar />
        </Box>
      )}

      <MobileDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        navItems={menuItems}
        expandedItems={expandedItems}
        onExpandClick={handleExpandClick}
        onNavigate={(item: string) => {
          handleNavigation(item);
          handleDrawerClose();
        }}
      />

      <SidebarDrawer
        open={sidebarDrawerOpen}
        onClose={() => setSidebarDrawerOpen(false)}
        navItems={navItems.filter((item: any) => {
          if (!item.roles) return true;
          return item.roles.includes(myInfo?.userType);
        })}
        handleLogout={handleLogout}
      />
    </Box>
  );
};

export default Topbar;
